import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import '../tabs/PaymentManagementTab.css';

/**
 * AddPaymentModal Component
 * 
 * A reusable modal for adding payments to appointments.
 * Supports normal payments, debt payment, and debt cancellation.
 * 
 * @param {Object} props
 * @param {boolean} props.show - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onConfirm - Callback when payment is confirmed (receives payment data)
 * @param {Object} props.currentPayment - Current payment object with appointmentId, debt, totalAmount, paidAmount
 * @param {Object} props.statistics - Statistics object with totalDebt
 * @param {Function} props.formatCurrency - Function to format currency values
 * @param {string} props.initialPaymentType - Initial payment type ('normal', 'debt_payment', 'cancellation')
 * @param {string} props.initialAmount - Initial payment amount
 * @param {string} props.initialNote - Initial payment note
 * @param {string} props.initialMethod - Initial payment method
 */
const AddPaymentModal = ({
  show,
  onClose,
  onConfirm,
  currentPayment,
  statistics,
  formatCurrency,
  initialPaymentType = 'normal',
  initialAmount = '',
  initialNote = '',
  initialMethod = 'Espèces'
}) => {
  const [paymentType, setPaymentType] = useState(initialPaymentType);
  const [paymentAmount, setPaymentAmount] = useState(initialAmount);
  const [paymentMethodType, setPaymentMethodType] = useState(initialMethod);
  const [paymentNote, setPaymentNote] = useState(initialNote);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update state when initial values change
  useEffect(() => {
    setPaymentType(initialPaymentType);
    setPaymentAmount(initialAmount);
    setPaymentMethodType(initialMethod);
    setPaymentNote(initialNote);
  }, [initialPaymentType, initialAmount, initialMethod, initialNote, show]);

  if (!show) return null;

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const paymentData = {
      amount: parseFloat(paymentAmount),
      paymentType,
      paymentMethod: paymentMethodType,
      note: paymentNote
    };

    setIsSubmitting(true);
    try {
      await onConfirm(paymentData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setPaymentType('normal');
    setPaymentAmount('');
    setPaymentMethodType('Espèces');
    setPaymentNote('');
    onClose();
  };

  return (
    <div className="pmt-modal-backdrop">
      <div className="pmt-modal">
        <div className="pmt-modal-header">
          <h2 className="pmt-modal-title">Ajouter un Paiement</h2>
          <button className="pmt-modal-close" onClick={handleModalClose}>
            <X size={24} />
          </button>
        </div>
        <div className="pmt-modal-body">
          {currentPayment && (
            <div className="pmt-info-box blue">
              <p className="pmt-info-box-label">Rendez-vous:</p>
              <p className="pmt-info-box-value">{currentPayment.appointmentId}</p>
              <p className="pmt-info-box-label" style={{ marginTop: '0.5rem' }}>
                {currentPayment.debt < 0 ? 'Crédit actuel:' : 'Dette actuelle:'}
              </p>
              <p className="pmt-info-box-amount red">
                {currentPayment.debt < 0 
                  ? `+${formatCurrency(Math.abs(currentPayment.debt))}` 
                  : formatCurrency(currentPayment.debt)
                }
              </p>
            </div>
          )}

          <div className="pmt-form-group">
            <label className="pmt-label">Type de paiement</label>
            <select
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              className="pmt-select"
            >
              <option value="normal">💳 Paiement Normal</option>
              <option value="debt_payment" disabled={statistics?.totalDebt <= 0}>
                💰 Règlement de Dette {statistics?.totalDebt <= 0 && '(Aucune dette)'}
              </option>
              <option value="cancellation" disabled={statistics?.totalDebt <= 0}>
                🎁 Annulation de Dette {statistics?.totalDebt <= 0 && '(Aucune dette)'}
              </option>
            </select>
            {paymentType === 'cancellation' && (
              <div style={{ backgroundColor: '#fef3c7', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#92400e', marginTop: '0.5rem' }}>
                <p style={{ margin: 0, marginBottom: '0.5rem' }}>
                  ⚠️ L'annulation de dette est un geste commercial. Le montant sera déduit de la dette sans encaissement réel.
                </p>
                <p style={{ margin: 0, fontWeight: 600 }}>
                  Montant maximum annulable: {formatCurrency(statistics?.totalDebt || 0)}
                </p>
                <button
                  type="button"
                  onClick={() => setPaymentAmount((statistics?.totalDebt || 0).toString())}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#f59e0b',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Annuler toute la dette
                </button>
              </div>
            )}
            {paymentType === 'debt_payment' && (
              <p style={{ backgroundColor: '#dbeafe', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#1e40af', marginTop: '0.5rem' }}>
                ℹ️ Règlement d'une dette précédente. Ce paiement sera enregistré dans le rendez-vous actuel.
              </p>
            )}
          </div>

          <div className="pmt-form-group">
            <label className="pmt-label">Montant du paiement</label>
            <input
              type="number"
              step="0.01"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              placeholder="0.00"
              className="pmt-input"
            />
            {paymentType === 'cancellation' && paymentAmount && parseFloat(paymentAmount) > (statistics?.totalDebt || 0) && (
              <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem', fontWeight: 600 }}>
                ❌ Le montant d'annulation ne peut pas dépasser la dette totale ({formatCurrency(statistics?.totalDebt || 0)})
              </p>
            )}
            {paymentType !== 'cancellation' && paymentAmount && currentPayment && parseFloat(paymentAmount) > currentPayment.debt && (
              <p style={{ color: '#10b981', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                ℹ️ Le montant dépasse la dette ({formatCurrency(currentPayment.debt)}). La dette sera mise à zéro.
                {parseFloat(paymentAmount) > currentPayment.totalAmount && (
                  <span style={{ display: 'block', marginTop: '0.25rem', fontWeight: 600 }}>
                    ⚠️ Surplus de paiement: +{formatCurrency(parseFloat(paymentAmount) + currentPayment.paidAmount - currentPayment.totalAmount)}
                  </span>
                )}
              </p>
            )}
          </div>

          {paymentType !== 'cancellation' && (
            <div className="pmt-form-group">
              <label className="pmt-label">Méthode de paiement</label>
              <select
                value={paymentMethodType}
                onChange={(e) => setPaymentMethodType(e.target.value)}
                className="pmt-select"
              >
                <option value="Espèces">Espèces</option>
                <option value="Carte bancaire">Carte bancaire</option>
                <option value="Chèque">Chèque</option>
                <option value="Virement">Virement</option>
                <option value="Mobile Money">Mobile Money</option>
              </select>
            </div>
          )}

          <div className="pmt-form-group">
            <label className="pmt-label">Note (optionnel)</label>
            <textarea
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              rows={3}
              placeholder="Ajouter une note..."
              className="pmt-textarea"
            />
          </div>

          {paymentAmount && parseFloat(paymentAmount) > 0 && currentPayment && (
            <div className="pmt-info-box green">
              <p className="pmt-info-box-label">
                {(currentPayment.debt - parseFloat(paymentAmount)) < 0 
                  ? 'Nouveau crédit après paiement:' 
                  : 'Nouvelle dette après paiement:'}
              </p>
              <p className="pmt-info-box-amount green">
                {(currentPayment.debt - parseFloat(paymentAmount)) < 0 
                  ? `+${formatCurrency(Math.abs(currentPayment.debt - parseFloat(paymentAmount)))}` 
                  : formatCurrency(Math.max(0, currentPayment.debt - parseFloat(paymentAmount)))
                }
              </p>
              <p className="pmt-info-box-label" style={{ marginTop: '0.5rem' }}>Montant total payé:</p>
              <p className="pmt-info-box-amount green">
                {formatCurrency(currentPayment.paidAmount + parseFloat(paymentAmount))}
              </p>
            </div>
          )}

          <div className="pmt-modal-actions">
            <button
              onClick={handleSubmit}
              aria-busy={isSubmitting}
              disabled={
                isSubmitting ||
                !paymentAmount || 
                parseFloat(paymentAmount) <= 0 || 
                (paymentType === 'cancellation' && parseFloat(paymentAmount) > (statistics?.totalDebt || 0))
              }
              className="pmt-btn pmt-btn-success"
            >
              {isSubmitting ? 'Veuillez patienter...' : 'Confirmer le Paiement'}
            </button>
            <button
              onClick={handleModalClose}
              className="pmt-btn pmt-btn-secondary"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentModal;
